import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import ReactMarkdown from 'react-markdown'
import rehypeSanitize from 'rehype-sanitize'
import { downloadFile, downloadMarkdown, getFileMarkdown } from '../api/files'
import type { FileResponse, MarkdownPreviewResponse } from '../types/api'
import Button from './Button'
import Modal from './Modal'

interface MarkdownPreviewModalProps {
  file: FileResponse | null
  isOpen: boolean
  onClose: () => void
}

export default function MarkdownPreviewModal({ file, isOpen, onClose }: MarkdownPreviewModalProps) {
  const [data, setData] = useState<MarkdownPreviewResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen || !file) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setData(null)
    getFileMarkdown(file.id)
      .then((res) => {
        if (!cancelled) setData(res)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Preview not available')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, file])

  if (!file) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={file.originalFilename} size="xl">
      <div className="space-y-4">
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && <p className="text-harbor-text-secondary text-sm">Loading preview…</p>}
          {error && (
            <p className="text-amber-400 text-sm">
              {error.includes('not available') || error.includes('Index')
                ? 'Markdown preview is not available yet. Index the file first.'
                : error}
            </p>
          )}
          {data && (
            <div className="text-harbor-text text-sm leading-relaxed space-y-3 [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:text-harbor-text [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-harbor-text [&_h3]:font-medium [&_h3]:text-harbor-text [&_p]:text-harbor-text-secondary [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_code]:bg-harbor-surface-raised [&_code]:px-1 [&_code]:rounded [&_pre]:bg-harbor-bg [&_pre]:p-3 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_a]:text-emerald-400">
              <ReactMarkdown rehypePlugins={[rehypeSanitize]}>{data.markdown}</ReactMarkdown>
            </div>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-harbor-border">
          <Button
            variant="secondary"
            disabled={!data}
            onClick={async () => {
              try {
                await downloadMarkdown(file.id, file.originalFilename)
              } catch {
                toast.error('Download failed')
              }
            }}
          >
            Download Markdown
          </Button>
          <Button
            variant="secondary"
            onClick={async () => {
              try {
                await downloadFile(file.id, file.originalFilename)
              } catch {
                toast.error('Download failed')
              }
            }}
          >
            Download original
          </Button>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  )
}
